import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { APP_RESUME_REFRESH_EVENT } from '../lib/appResumeRefresh';

const CDN_REFRESH_COOLDOWN_MS = 5_000;

type UseCdnRefreshTokenOptions = {
  active?: boolean;
  key: string | null;
  onInvalidate?: (() => void) | null;
};

export const useCdnRefreshToken = ({
  active = true,
  key,
  onInvalidate = null,
}: UseCdnRefreshTokenOptions): string | null => {
  const [revision, setRevision] = useState(() => Date.now());
  const lastStateKeyRef = useRef(`${active ? '1' : '0'}:${key || ''}`);
  const lastRefreshAtRef = useRef(0);
  const invalidateRef = useRef<(() => void) | null>(onInvalidate);

  useEffect(() => {
    invalidateRef.current = onInvalidate;
  }, [onInvalidate]);

  useEffect(() => {
    const nextStateKey = `${active ? '1' : '0'}:${key || ''}`;
    if (nextStateKey === lastStateKeyRef.current) return;

    lastStateKeyRef.current = nextStateKey;
    lastRefreshAtRef.current = 0;

    if (active && key) {
      setRevision(Date.now());
    }
  }, [active, key]);

  useEffect(() => {
    if (!active || !key || typeof window === 'undefined') {
      return undefined;
    }

    const triggerRefresh = () => {
      const now = Date.now();
      if (now - lastRefreshAtRef.current < CDN_REFRESH_COOLDOWN_MS) {
        return;
      }
      lastRefreshAtRef.current = now;
      setRevision(now);
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      triggerRefresh();
    };

    const handleAppResumeRefresh = () => {
      triggerRefresh();
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener(APP_RESUME_REFRESH_EVENT, handleAppResumeRefresh);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener(APP_RESUME_REFRESH_EVENT, handleAppResumeRefresh);
    };
  }, [active, key]);

  const refreshToken = useMemo(() => {
    if (!active || !key) return null;
    return `${key}-${revision}`;
  }, [active, key, revision]);

  useLayoutEffect(() => {
    if (!refreshToken) return;
    invalidateRef.current?.();
  }, [refreshToken]);

  return refreshToken;
};
